// ---- the round ----
// One word at a time: its picture in the tray, its name spoken, its letters in
// the sky. Every tap is answered here — the right chip flies to its slot, any
// other names itself and shakes it off, a tap on something in the field gets
// its name, a tap on the grown-up gets their gesture (and the name, while it is
// still owed), a tap on nothing still pops — and a finished word puts the thing
// in the field and deals the next.
//
// This is the half of a spelling game that is the same in every one of them,
// down to the awkward parts: the name owed until it has actually been HEARD,
// the hello the first round waits on, and the beat between a finished word and
// the next deal. What differs is who lives in the field and who talks about it,
// and all of that comes in through setRound().
import * as THREE from 'three';
import {after,audioReady,clipMs,clips,interrupt,reserve,sayLetter,sayWord,sndNudge,sndPoof,sndPop,sndYay,speaking,speechGen,voiceEnd,wordMs} from './audio';
import {deal,hitChip,launch,type Chip} from './chips';
import {facesOf,unitsOf} from './letters';
import {cam} from './scene';
import {bow,buildSlots,fillSlot,hideHint,promptTaps,setCount,showPrompt} from './tray';
import {clamp} from './util';

export interface Spec{w:string}
export interface RoundCfg{
  roster:readonly Spec[];                    // the words, shortest first — the order rounds are dealt in
  ready:(w:string)=>boolean;                 // is this word's model here yet? only a ready one is dealt
  intro:string;                              // the grown-up's hello, said once a visit
  greet:(t:number)=>void;                    // them saying it
  // A finished word, in two beats, because the order matters and is audible:
  // the thing lands (arrive), the poof sounds and the badge ticks, and only
  // THEN does the grown-up answer it. Folding the answer into the arrival puts
  // their line before the poof and costs one draw of the random stream in a
  // different place — which is exactly the kind of silent drift this whole
  // split is trying not to introduce.
  arrive:(w:string,t:number)=>unknown;
  // What the grown-up says about it. Returns how long they will still be
  // talking, so the next round waits for them.
  answer?:(thing:unknown,t:number)=>number;
  count:()=>number;                          // what the badge shows
  elder:THREE.Object3D;                      // the grown-up, for the raycast
  poke:(t:number,say?:()=>void)=>void;       // a tap on them
  pickables:()=>THREE.Object3D[];            // everything else a tap can land on
  tapped?:(o:THREE.Object3D,t:number)=>void; // what a tap on one of those does
  // What opening a round puts in front of the child, when it is not the word's
  // letters in the sky. The tracing board (trace.ts) opens here instead, and
  // says the word is done through complete() rather than a chip landing in its
  // last slot.
  deal?:(w:string,t:number)=>void;
}
let cfg:RoundCfg|null=null;
const C=():RoundCfg=>{if(!cfg)throw new Error('round: setRound() has not been called');return cfg;};
export function setRound(c:RoundCfg){cfg=c;}

let target:Spec|null=null, queue:Spec[]=[], step=0;
let hintT=0, wrongT=-1e9, dealt=false, owed=false, announcing=false;
// The grown-up's hello opens the game, once a visit: 'due' until it is said or found
// unrecorded, 'saying' while the letters wait in the tray for it, 'done' after.
// The first round gives its clip HELLO_WAIT ms to arrive before opening without it.
let intro:'due'|'saying'|'done'='due', introBy=0;
const HELLO_WAIT=6000;
export const round={get target(){return target},get step(){return step},get dealt(){return dealt},get intro(){return intro},get owed(){return owed}};
// A round opens in two steps, each retried from the loop: the letters rise out
// of the tray (deal), then the name is said. The first round of a visit opens
// on his hello (INTRO, data.ts): the letters stay in the tray until he has said
// it — the line itself waits for the first touch, as every voice does — and
// rise as his voice stops. His lines are recorded only, so with no take the
// round opens at once, as every later one does.
// The name is owed until it has been heard. Audio may still be locked (the
// first touch unlocks it), the clip may still be on its way, he may be
// answering a tap — a letter, a line of his — and a tap in the beat before he
// says it moves the generation on and drops it: a child who opened the game
// with two taps on him used to get one of his lines and never the name. So it
// is retried from the loop until it has been heard, each time he is free, and
// a tap on him or on the picture while it is owed says it at once (sayName).
export function announceRound(t:number){
  if(!target)return;
  if(!dealt){
    if(intro==='due'){
      const buf=clips['R'+C().intro];
      if(buf&&buf!=='missing'){if(!audioReady())return;C().greet(t);hideHint();intro='saying';return;}   // the touch came: the hint's job is done, even one on the picture
      if(buf===undefined&&t<introBy)return;     // still on its way
      intro='done';                             // no take, or none in time
    }
    if(intro==='saying'){if(speaking(t))return;intro='done';}
    (C().deal??deal)(target.w,t); dealt=true; hintT=t; wrongT=-1e9;
  }
  if(!owed||announcing||!audioReady()||clips['W'+target.w]===undefined||speaking(t))return;
  announcing=true; interrupt(); const w=target.w, g=speechGen();
  reserve(300+wordMs(w));                        // the field is quiet from now until he has said it
  setTimeout(()=>{announcing=false;if(owed&&g===speechGen())speakName(w);},300);   // cut in on: still owed, said once he is free again
}
// The debt is settled once the first saying has been heard to its end; a tap
// that cuts it short (after, audio.ts) leaves the name owed. Nothing plays
// until the clip is here, and the debt stands until it has.
function speakName(w:string){if(sayWord(w))after(clipMs('W'+w),()=>{if(target&&target.w===w)owed=false;});}
// The name, now: what the picture says, and what a tap on him says while it is
// still owed — once the letters are up. Before that he is saying, or about to
// say, his hello, and neither tap cuts it: the name stays owed and follows the
// letters up.
export function sayName(){if(target&&dealt){interrupt();speakName(target.w);}}
promptTaps(sayName);
export function nextRound(t:number){
  // only words whose model is here are dealt; the first round waits for one
  if(!queue.length){
    queue=C().roster.filter(a=>C().ready(a.w));
    if(!queue.length){setTimeout(()=>nextRound(performance.now()),300);return;}
    if(queue.length>1&&queue[0]===target)queue.push(queue.shift()!);   // never the same word twice running
  }
  target=queue.shift()!; step=0; buildSlots(target.w,step);
  showPrompt(target.w);
  if(!introBy)introBy=t+HELLO_WAIT;             // the first round: how long its letters wait for his hello to arrive
  dealt=false; owed=true; announceRound(t);
}
// The thing arrives, with a poof and one more on the badge. The grown-up's
// answer is NOT part of this: it belongs to finishWord, after the poof, and
// keeping it out is what lets the ?dbg handle drop something into the field
// without also making them talk about it.
export function spawn(word:string,t:number){
  const thing=C().arrive(word,t);
  sndPoof();
  setCount(C().count());
  return thing;
}
// The word is done. The tray takes its bow at once, but the animal waits for
// the end of the letter he is still saying: it arrives to his welcome and to
// nothing else. His moment then runs on to the next deal, so no cry starts in
// the gap before he says the new name.
function finishWord(t:number){
  bow(); sndYay(); owed=false;                   // a name still owed is forgiven: the welcome is what follows
  setTimeout(()=>{
    const t1=performance.now(), thing=spawn(target!.w,t1), talk=C().answer?.(thing,t1)??0;
    const wait=clamp(Math.max(2100,talk)+500,2400,6800);
    reserve(wait); setTimeout(()=>nextRound(performance.now()),wait);
  },Math.max(0,voiceEnd()-t));
}
// The word is done some other way than its last chip landing: the tracing
// board's word has come together (trace.ts). Once a round, and only once its
// letters are up.
let finished:Spec|null=null;
export function complete(t:number){if(!target||!dealt||finished===target)return;finished=target;finishWord(t);}
// a chip has reached slot i
export function landed(i:number,t:number){fillSlot(i);if(target&&i===unitsOf(target.w).length-1)finishWord(t);}
// The chip is judged by what is drawn on it: the بَ of بَبَّغَاء is the first
// letter and the بَّ the second, and either is named باء when it is tapped.
function tapChip(c:Chip,t:number){
  c.popT=t; interrupt(); sayLetter(c.ch);
  if(!target||c.face!==facesOf(target.w)[step]){c.wobT=t;wrongT=t;sndNudge();return;}   // still named; nothing to undo
  const i=step++; hintT=t; sndPop(i);
  launch(c,i,t);
}
const ray=new THREE.Raycaster(), v2=new THREE.Vector2();
export function tap(px:number,py:number,t:number){
  hideHint();
  const c=hitChip(px,py); if(c){tapChip(c,t);return;}
  ray.setFromCamera(v2.set(px/innerWidth*2-1,1-py/innerHeight*2),cam);
  const h=ray.intersectObjects([C().elder,...C().pickables()],true)[0];
  if(h&&h.object.userData.who==='elder'){C().poke(t,dealt&&owed&&target&&clips['W'+target.w]!==undefined?sayName:undefined);return;}   // the name he owes, once the letters are up, else his point alone
  if(h&&h.object.userData.ele){C().tapped?.(h.object,t);return;}
  sndPop();                                     // a tap on nothing still answers
}
// The letter he needs starts breathing after a few quiet seconds, and right
// away after a tap that wasn't it. A hint, never a correction.
export function wanted(t:number){
  if(C().deal)return {need:null,nudge:false};    // no chips, and no slot to breathe
  const need=(dealt&&target&&facesOf(target.w)[step])||null, nudge=!!(need&&(t-hintT>4500||t-wrongT<2400));
  return {need,nudge};
}
export function rebuildTray(){buildSlots(target?target.w:null,step);}   // after a resize
